/**
 * ANNUITY PRICES — read from the published rate sheet, never invented.
 *
 * THE FAULT THIS REPLACES (25 September 2026). Annuity income came from a base rate plus a flat age
 * step, invented in code. Checked against Challenger's published adviser sheet it paid roughly HALF
 * the market at every age and in every shape: 4.80% where the sheet said 8.30% at 65, 5.80% where
 * the sheet said 10.49% at 75. Because annuity income feeds the estate, the cashflow and the means
 * test, that understatement flowed into every module and made the suggestion engine rank annuity
 * strategies worse than they deserve. Three separate things were wrong — the base was too low, the
 * age step was flat when the real one accelerates, and the cost of CPI indexation was set at 0.60
 * percentage points when the sheet's own gap is 1.49 to 1.81.
 *
 * WHAT CHANGED. Rates are now DATA. `annuityRates.generated.ts` holds the published sheet, imported
 * by `scripts/import-annuity-rates.mjs`; this file is the only thing that reads it, and it does not
 * invent anything the sheet does not say. Where the sheet stops, it says so rather than guessing
 * quietly: every answer carries `extrapolated`, and the screens print a note when it is true.
 *
 * WHICH COLUMN IS WHICH SHAPE. The sheet prices two liquidity options, and they are exactly our two
 * client-facing choices:
 *   Enhanced  — no death benefit, no withdrawal value, highest income. Our "nil residual".
 *   Flexible  — a withdrawal period and a death benefit on the capital access schedule, less income.
 *               Our "capital returned".
 * The guaranteed shape (the whole amount to the estate whenever you die) is neither: it is an
 * annuity bought together with a life policy, and the sheet prices it as one product, so it has its
 * own table and its own function here.
 *
 * WHAT THE SHEET DOES NOT PUBLISH. Lifetime rates stop at age 75. Above that we extrapolate from the
 * 70-to-75 slope and flag it. That errs LOW: the one real data point we have above 75 (the annuity
 * inside the guaranteed product, from Challenger's own calculator) prices an 85-year-old man's
 * annuity slice near 19.9%, where this extrapolation gives about 13.1%. Erring low understates the
 * annuity rather than flattering it, which is the safe direction, but the actuary should look at it.
 */

import { ANNUITY_RATES } from './constants/annuityRates.generated.ts';

export type RateSex = 'male' | 'female' | null | undefined;

/** How long a sheet may sit before the app starts saying it is old. */
export const RATE_SHEET_STALE_AFTER_DAYS = 45;

export interface AnnuityRateAnswer {
    /** The yearly payment as a fraction of the purchase price, e.g. 0.1049 for 10.49%. */
    rate: number;
    /** True when the sheet does not publish this age or term and we extrapolated beyond it. */
    extrapolated: boolean;
    /** A plain sentence naming where the figure came from, for the screen and the advice document. */
    basis: string;
}

/** What the current sheet is, and whether it is getting old. Screens quote this. */
export function annuityRateSheet(today: Date = new Date()): {
    source: string;
    effectiveFrom: string;
    effectiveTo: string;
    importedAt: string;
    ageInDays: number;
    stale: boolean;
    label: string;
} {
    const from = ANNUITY_RATES.effectiveFrom;
    const ageInDays = Math.max(0, Math.round((today.getTime() - new Date(from + 'T00:00:00Z').getTime()) / 86_400_000));
    return {
        source: ANNUITY_RATES.source,
        effectiveFrom: from,
        effectiveTo: ANNUITY_RATES.effectiveTo,
        importedAt: ANNUITY_RATES.importedAt,
        ageInDays,
        stale: ageInDays > RATE_SHEET_STALE_AFTER_DAYS,
        label: `the published rates for the week of ${formatSheetDate(from)}`,
    };
}

function formatSheetDate(iso: string): string {
    const d = new Date(iso + 'T00:00:00Z');
    if (Number.isNaN(d.getTime())) return iso;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// ---------------------------------------------------------------------------- interpolation

interface Interpolated {
    value: number;
    extrapolated: boolean;
}

/**
 * Read `table` at `key`, interpolating between the keys it has. Outside the published range it
 * carries the nearest segment's slope onward and says so, rather than clamping — a clamp would
 * silently price an 88-year-old as if he were 75.
 */
function interpolate(table: Record<string, number>, key: number): Interpolated {
    const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
    // Every key below is read from the table itself, so a miss is a bug, not a missing age; the
    // checked lookup says so loudly rather than letting an undefined turn into NaN downstream.
    const at = (k: number): number => {
        const v = table[String(k)];
        if (v === undefined) throw new Error(`annuity rate table has no entry for ${k}`);
        return v;
    };
    const keyAt = (i: number): number => {
        const k = keys[i];
        if (k === undefined) throw new Error(`annuity rate table index ${i} out of range`);
        return k;
    };
    if (keys.length === 0) return { value: 0, extrapolated: true };
    if (keys.length === 1) return { value: at(keyAt(0)), extrapolated: keyAt(0) !== key };

    const exact = table[String(key)];
    if (exact !== undefined) return { value: exact, extrapolated: false };

    const first = keyAt(0);
    const last = keyAt(keys.length - 1);

    const segment = (lo: number, hi: number, extrapolated: boolean): Interpolated => {
        const vLo = at(lo);
        const vHi = at(hi);
        const t = (key - lo) / (hi - lo);
        return { value: vLo + (vHi - vLo) * t, extrapolated };
    };

    if (key < first) return segment(keyAt(0), keyAt(1), true);
    if (key > last) return segment(keyAt(keys.length - 2), last, true);

    for (let i = 1; i < keys.length; i++) {
        if (key <= keyAt(i)) return segment(keyAt(i - 1), keyAt(i), false);
    }
    return { value: at(last), extrapolated: true };
}

/** Pull one liquidity option out of a [Flexible, Enhanced] block into a plain age → dollars map. */
function optionTable(block: Readonly<Record<string, readonly [number, number]>>, option: 0 | 1): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [age, pair] of Object.entries(block)) out[age] = pair[option];
    return out;
}

/**
 * The sex to price against when the fact-find does not record one.
 *
 * FEMALE, deliberately, and note this is the OPPOSITE default to `lifeExpectancyAt`, which reads an
 * unknown sex as male. Each default is the cautious direction for its own job. A woman's longer life
 * expectancy buys her a LOWER annuity payment at the same age, so pricing an unknown client as
 * female never overstates the income a strategy would produce. The life table's default runs the
 * other way because there a SHORTER expectancy is the cautious answer: it keeps the capital access
 * schedule inside what the rules allow.
 */
function priceAs(sex: RateSex): 'male' | 'female' {
    return sex === 'male' ? 'male' : 'female';
}

// ---------------------------------------------------------------------------- lifetime

export interface LifetimeRateQuery {
    age: number;
    sex?: RateSex;
    /** True for a CPI-indexed annuity. The sheet prices indexation as its own table, not a discount. */
    indexation?: boolean;
    /** 0 for nil residual (Enhanced), 1 for the full amount back (Flexible). Between the two blends. */
    residualCapital?: number;
    /** Set for a reversionary annuity, where the income continues to the survivor. */
    partnerAge?: number | null;
}

/**
 * The yearly payment rate for a plain lifetime annuity.
 *
 * The residual blend is a straight line between the two options the sheet prices. Challenger sells
 * intermediate shapes but does not publish them, so a client asking for, say, 50% back is quoted
 * halfway and told on screen that the exact figure needs a quote.
 */
export function lifetimeAnnuityRate(query: LifetimeRateQuery): AnnuityRateAnswer {
    const { age, sex, indexation = false, residualCapital = 0, partnerAge = null } = query;
    const block = indexation ? ANNUITY_RATES.lifetime.cpi : ANNUITY_RATES.lifetime.noIndexation;
    const sexKey = priceAs(sex);
    const table = block[sexKey] as unknown as Record<string, readonly [number, number]>;

    const residual = Math.min(1, Math.max(0, residualCapital || 0));
    const enhanced = interpolate(optionTable(table, 1), age);
    const flexible = interpolate(optionTable(table, 0), age);

    // Per $100,000, blended between the two published shapes, then expressed as a fraction.
    const perHundredThousand = enhanced.value + (flexible.value - enhanced.value) * residual;
    let rate = perHundredThousand / 100_000;
    let extrapolated = enhanced.extrapolated || flexible.extrapolated;

    if (partnerAge != null && partnerAge > 0) {
        const joint = reversionaryRate(block.reversionary, age, partnerAge, residual);
        if (joint) {
            rate = joint.value / 100_000;
            extrapolated = extrapolated || joint.extrapolated;
        }
    }

    const sheet = annuityRateSheet();
    const shape = residual >= 0.999 ? 'with the full amount back' : residual <= 0.001 ? 'with nothing back' : `with ${Math.round(residual * 100)}% back`;
    const basis = extrapolated
        ? `Estimated from ${sheet.label}, which stop at age 75. A quote will differ.`
        : `From ${sheet.label}, for a lifetime annuity ${shape}${indexation ? ', indexed to prices' : ''}.`;

    return { rate: clampRate(rate), extrapolated, basis };
}

/**
 * What a reversionary annuity pays, where the income continues to the survivor.
 *
 * The sheet quotes these against AGE PAIRS rather than single ages — M65/F60, M65/F65, M70/F65 and
 * M70/F70 — so they cannot be looked up the way the single-life tables can. The four pairs are
 * monotonic in the AVERAGE of the two ages (62.5, 65, 67.5 and 70), which is what you would expect,
 * since a reversionary annuity is priced on how long at least one of the two will live. So we key
 * the table on that average and read it directly. Using all four published pairs beats deriving a
 * discount from the two same-age ones, and it gets the direction right: a younger partner means the
 * income runs longer and therefore pays LESS, which the sheet's own M65/F60 row confirms.
 *
 * This replaces the flat 0.4-percentage-point joint discount the code used to apply, which happened
 * to be about right at 65 and too small by half at 70.
 */
function reversionaryRate(
    reversionary: Readonly<Array<{ readonly maleAge: number; readonly femaleAge: number; readonly flexible: number; readonly enhanced: number }>>,
    age: number,
    partnerAge: number,
    residual: number
): Interpolated | null {
    const byAverageAge: Record<string, number> = {};
    for (const row of reversionary) {
        const average = (row.maleAge + row.femaleAge) / 2;
        byAverageAge[String(average)] = row.enhanced + (row.flexible - row.enhanced) * residual;
    }
    if (Object.keys(byAverageAge).length < 2) return null;
    return interpolate(byAverageAge, (age + partnerAge) / 2);
}

// ---------------------------------------------------------------------------- guaranteed

/**
 * The yearly payment rate for the shape that guarantees the WHOLE amount to the estate, whenever
 * death comes. No single annuity does this, so the sheet prices an annuity and a life policy bought
 * together, per $100,000 of total money invested, before any adviser fee.
 *
 * Two things about this table surprise people, and both belong on screen. The rate barely moves with
 * age — 5.79% at 70 and 5.91% at 100 — because the product is priced as a whole, and as the client
 * gets older the money simply shifts out of the annuity and into the insurance. And it is published
 * from age 70 to 100, so unlike the plain shapes it needs no extrapolation for a client in care.
 */
export function guaranteedAnnuityRate(query: { age: number; sex?: RateSex }): AnnuityRateAnswer {
    const sexKey = priceAs(query.sex);
    const table = ANNUITY_RATES.carePlus[sexKey] as unknown as Record<string, number>;
    const found = interpolate(table, query.age);
    const sheet = annuityRateSheet();
    return {
        rate: clampRate(found.value / 100_000),
        extrapolated: found.extrapolated,
        basis: found.extrapolated
            ? `Estimated from ${sheet.label}, which cover ages 70 to 100. A quote will differ.`
            : `From ${sheet.label}, before any adviser fee.`,
    };
}

// ---------------------------------------------------------------------------- fixed term

/**
 * The EARNING rate on a fixed-term annuity — not its payment. The payment on a term annuity is this
 * earning plus the slice of capital that is not coming back at the end, and `buildAnnuityImpact`
 * does that amortisation. The sheet publishes the earning rate as a percent, by term, for capital
 * fully returned (RCV 100) and not returned at all (RCV 0); anything between blends.
 */
export function fixedTermAnnuityRate(query: { termYears: number; residualCapital?: number }): AnnuityRateAnswer {
    const residual = Math.min(1, Math.max(0, query.residualCapital || 0));
    const term = Math.max(1, query.termYears || 1);

    const yearlyOnly = (rows: Readonly<Record<string, { readonly monthly: number | null; readonly yearly: number | null }>>): Record<string, number> => {
        const out: Record<string, number> = {};
        for (const key of Object.keys(rows)) {
            // The one-year term has no yearly-payment rate published; its monthly rate is the closest
            // thing the sheet offers and is the more conservative of the two.
            const row = rows[key];
            const value = row ? (row.yearly ?? row.monthly) : null;
            if (value != null) out[key] = value;
        }
        return out;
    };

    const rcv0 = interpolate(yearlyOnly(ANNUITY_RATES.fixedTerm.rcv0 as never), term);
    const rcv100 = interpolate(yearlyOnly(ANNUITY_RATES.fixedTerm.rcv100 as never), term);
    const percent = rcv0.value + (rcv100.value - rcv0.value) * residual;
    const sheet = annuityRateSheet();
    const extrapolated = rcv0.extrapolated || rcv100.extrapolated;

    return {
        rate: clampRate(percent / 100),
        extrapolated,
        basis: extrapolated
            ? `Estimated from ${sheet.label}, which cover terms of 1 to 20 years. A quote will differ.`
            : `From ${sheet.label}, for a ${Math.round(term)}-year term.`,
    };
}

/**
 * The last line of defence. Nothing the sheet publishes comes close to these bounds, so this only
 * ever catches an extrapolation that has run away or a mis-imported table that slipped the
 * importer's own checks.
 */
function clampRate(rate: number): number {
    if (!Number.isFinite(rate)) return 0.05;
    return Math.min(0.25, Math.max(0.02, rate));
}
